import React, { useState } from 'react';
import { Activity, TrendingUp, AlertCircle, Ruler } from 'lucide-react';

export default function LiftingFormCalculator() {
  const [measurements, setMeasurements] = useState({
    height: '',
    sittingHeight: '',
    armLength: '',
    thighLength: '',
    shoulderWidth: '',
    bodyweight: '',
    sex: 'male',
    unit: 'inches'
  });

  const [results, setResults] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    squat: false,
    deadlift: false,
    clean: false,
    snatch: false,
    overhead: false
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const getLiftSummary = (liftAdvice, liftStandards) => {
    // Get the BUILD line for preview
    const buildLine = liftAdvice.find(tip => tip.startsWith('[BUILD]'));
    const buildText = buildLine ? buildLine.replace('[BUILD] YOUR BUILD: ', '') : '';
    
    // Get standards range
    const novice = liftStandards.levels.novice;
    const elite = liftStandards.levels.elite;
    
    return { buildText, standardsRange: `${novice}-${elite} lbs` };
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setMeasurements(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Prevent scroll wheel from changing number inputs
  const handleNumberWheel = (e) => {
    e.target.blur();
  };

  const analyzeMeasurements = () => {
    const { height, sittingHeight, armLength, thighLength, shoulderWidth, bodyweight, sex } = measurements;
    
    // Convert to numbers
    const h = parseFloat(height);
    const sh = parseFloat(sittingHeight);
    const a = parseFloat(armLength);
    const th = parseFloat(thighLength);
    const s = parseFloat(shoulderWidth);
    const bw = parseFloat(bodyweight);

    if (!h || !sh || !a || !th || !s || !bw) {
      alert('Please fill in all measurements including bodyweight');
      return;
    }

    // Calculate leg length from sitting height
    const l = h - sh;

    // Validation: Check if measurements make sense
    if (sh >= h) {
      alert('⚠ WARNING: Your sitting height cannot be greater than or equal to your total height. Please double-check your measurements.');
      return;
    }

    if (l < 10 || sh < 20) {
      alert('⚠ WARNING: Your measurements seem unusually small. Please verify you entered the correct values.');
      return;
    }

    if (a > h * 0.6) {
      alert('⚠ WARNING: Your arm length seems too long relative to your height. Please double-check your measurements.');
      return;
    }

    if (s > h * 0.35) {
      alert('⚠ WARNING: Your shoulder width seems too wide relative to your height. Please double-check your measurements.');
      return;
    }

    if (th > l * 0.75) {
      alert('⚠ WARNING: Your thigh length seems too long relative to your total leg length. Please double-check your measurements.');
      return;
    }

    // Calculate ratios
    const armToHeight = a / h;
    const legToHeight = l / h;
    const sittingHeightRatio = sh / h;
    const femurToHeight = th / h;
    const shoulderToHeight = s / h;
    
    // NEW: Calculate tibia (shin) length and ratio
    const tibiaLength = l - th;
    const tibiaToFemur = tibiaLength / th;

    // Analyze proportions using research-based thresholds
    // Arm ratio: average 45-47% (Ethiopian study)
    const longArms = armToHeight > 0.47;
    const shortArms = armToHeight < 0.43;
    
    // Leg ratio: average 48% (inverse of sitting height 52%)
    const longLegs = legToHeight > 0.50;
    const shortLegs = legToHeight < 0.46;
    
    // Sitting height ratio: average 52% (standard anthropometric measure)
    const longTorso = sittingHeightRatio > 0.54;
    const shortTorso = sittingHeightRatio < 0.50;
    
    // Femur ratio: average 25-26% of height (Dempster + ChatGPT consensus)
    const longFemur = femurToHeight > 0.27;
    const shortFemur = femurToHeight < 0.23;
    
    // NEW: Tibia-to-femur ratio: average 1.15-1.25
    const longTibia = tibiaToFemur > 1.30;
    const shortTibia = tibiaToFemur < 1.10;
    
    // Shoulder ratio: average 22-23% (CDC data via ChatGPT)
    const broadShoulders = shoulderToHeight > 0.24;

    // COMPOUND BUILD TYPE DETECTION (Tier 1 - Critical Combinations)
    const buildTypes = {
      isPowerlifter: shortArms && shortFemur,           // Elite squat/press, poor deadlift
      isDeadlifter: longArms && longFemur,              // Elite deadlift, challenged squat
      isSquatChallenged: longFemur && shortTibia,       // Worst squat combination
      isSquatGifted: shortFemur && longTibia,           // Best squat combination
      hasBackStrainRisk: longFemur && longTorso,        // Injury risk - long levers
      isOlympicBuild: longArms && longLegs              // Good for Olympic lifts
    };

    const proportionData = {
      legToHeight: (legToHeight * 100).toFixed(1),
      sittingHeightRatio: (sittingHeightRatio * 100).toFixed(1),
      femurToHeight: (femurToHeight * 100).toFixed(1),
      armToHeight: (armToHeight * 100).toFixed(1),
      shoulderToHeight: (shoulderToHeight * 100).toFixed(1),
      tibiaToFemur: tibiaToFemur.toFixed(2)
    };

    const recommendations = {
      squat: generateSquatAdvice(longLegs, shortLegs, longTorso, shortTorso, longFemur, shortFemur, longTibia, shortTibia, proportionData, buildTypes, shortArms, longArms),
      deadlift: generateDeadliftAdvice(longArms, shortArms, longLegs, longFemur, longTorso, shortFemur, proportionData, buildTypes),
      clean: generateCleanAdvice(longArms, shortArms, longLegs, longTorso, shortFemur, longFemur, proportionData, buildTypes),
      snatch: generateSnatchAdvice(longArms, shortArms, longLegs, longFemur, s, h, proportionData, buildTypes),
      overhead: generateOverheadAdvice(longArms, shortArms, longTorso, proportionData, buildTypes),
      strengthStandards: calculateStrengthStandards(bw, sex, longArms, shortArms, longFemur, shortFemur, longTibia, shortTibia, longLegs),
      bodyweight: bw,
      sex: sex,
      proportions: {
        armToHeight: (armToHeight * 100).toFixed(1),
        legToHeight: (legToHeight * 100).toFixed(1),
        sittingHeightRatio: (sittingHeightRatio * 100).toFixed(1),
        femurToHeight: (femurToHeight * 100).toFixed(1),
        shoulderToHeight: (shoulderToHeight * 100).toFixed(1),
        tibiaToFemur: tibiaToFemur.toFixed(2),
        type: determineBodyType(longArms, longLegs, longTorso, longFemur, broadShoulders, shortArms, shortFemur, buildTypes)
      }
    };

    setResults(recommendations);
  };

  const determineBodyType = (longArms, longLegs, longTorso, longFemur, broadShoulders, shortArms, shortFemur, buildTypes) => {
    // Check compound build types FIRST
    if (buildTypes.isPowerlifter) return "Powerlifter build (short arms + short femurs)";
    if (buildTypes.isDeadlifter) return "Deadlifter build (long arms + long femurs)";
    if (buildTypes.isSquatGifted) return "Olympic squatter (short femurs + long tibias)";
    if (buildTypes.isSquatChallenged) return "Squat challenged (long femurs + short tibias)";
    if (buildTypes.hasBackStrainRisk) return "Back strain risk (long femurs + long torso)";
    if (buildTypes.isOlympicBuild) return "Olympic build (long arms + long legs)";
    
    // Individual traits
    const traits = [];
    
    if (shortArms) traits.push("short arms");
    if (shortFemur) traits.push("short femurs");
    if (longArms) traits.push("long arms");
    if (longLegs) traits.push("long legs");
    if (longTorso) traits.push("long torso");
    if (longFemur) traits.push("long femurs");
    if (broadShoulders) traits.push("broad shoulders");
    
    if (traits.length === 0) return "Proportionate build";
    if (traits.length === 1) {
      const trait = traits[0];
      return trait.charAt(0).toUpperCase() + trait.slice(1);
    }
    
    return traits.map((t, i) => i === 0 ? t.charAt(0).toUpperCase() + t.slice(1) : t).join(", ");
  };

  const calculateStrengthStandards = (bodyweight, sex, longArms, shortArms, longFemur, shortFemur, longTibia, shortTibia, longLegs) => {
    // Base strength standards (bodyweight multipliers)
    const standards = {
      squat: {
        male: { novice: 0.75, beginner: 1.0, intermediate: 1.5, advanced: 2.0, elite: 2.5 },
        female: { novice: 0.5, beginner: 0.75, intermediate: 1.1, advanced: 1.5, elite: 1.9 }
      },
      deadlift: {
        male: { novice: 0.85, beginner: 1.25, intermediate: 1.75, advanced: 2.5, elite: 3.0 },
        female: { novice: 0.6, beginner: 1.0, intermediate: 1.4, advanced: 1.9, elite: 2.4 }
      },
      clean: {
        male: { novice: 0.5, beginner: 0.75, intermediate: 1.1, advanced: 1.5, elite: 1.6 },
        female: { novice: 0.35, beginner: 0.55, intermediate: 0.8, advanced: 1.1, elite: 1.2 }
      },
      snatch: {
        male: { novice: 0.35, beginner: 0.55, intermediate: 0.75, advanced: 1.0, elite: 1.25 },
        female: { novice: 0.25, beginner: 0.4, intermediate: 0.55, advanced: 0.75, elite: 0.95 }
      },
      overhead: {
        male: { novice: 0.35, beginner: 0.5, intermediate: 0.75, advanced: 1.0, elite: 1.35 },
        female: { novice: 0.2, beginner: 0.35, intermediate: 0.5, advanced: 0.7, elite: 0.9 }
      }
    };

    const calculateLift = (lift, baseStandards) => {
      const levels = {};
      let adjustmentNote = "";
      let adjustmentPercent = 0;

      Object.keys(baseStandards).forEach(level => {
        let weight = bodyweight * baseStandards[level];
        
        // Apply proportion adjustments
        if (lift === 'squat') {
          if (longFemur && shortTibia) {
            weight *= 0.88; // -12% for worst case
            adjustmentPercent = -12;
            adjustmentNote = "Adjusted -12% (long femurs + short tibias = harder mechanics)";
          } else if (longFemur) {
            weight *= 0.93; // -7%
            adjustmentPercent = -7;
            adjustmentNote = "Adjusted -7% (long femurs = more forward lean required)";
          } else if (shortFemur && longTibia) {
            weight *= 1.08; // +8% for best case
            adjustmentPercent = +8;
            adjustmentNote = "Adjusted +8% (short femurs + long tibias = ideal squat mechanics!)";
          } else if (shortFemur) {
            weight *= 1.05; // +5%
            adjustmentPercent = +5;
            adjustmentNote = "Adjusted +5% (short femurs = squat advantage)";
          }
        }
        
        if (lift === 'deadlift') {
          if (longArms) {
            weight *= 1.10; // +10%
            adjustmentPercent = +10;
            adjustmentNote = "Adjusted +10% (long arms = shorter ROM, mechanical advantage!)";
          } else if (shortArms) {
            weight *= 0.88; // -12%
            adjustmentPercent = -12;
            adjustmentNote = "Adjusted -12% (short arms = longer ROM, harder pull)";
          }
        }

        if (lift === 'clean') {
          if (longArms && longLegs) {
            weight *= 0.95; // -5%
            adjustmentPercent = -5;
            adjustmentNote = "Adjusted -5% (long limbs = longer pull + deeper catch)";
          } else if (shortFemur && longTibia) {
            weight *= 1.05; // +5%
            adjustmentPercent = +5;
            adjustmentNote = "Adjusted +5% (good proportions for front squat catch)";
          }
        }

        if (lift === 'snatch') {
          if (longArms) {
            weight *= 0.93; // -7%
            adjustmentPercent = -7;
            adjustmentNote = "Adjusted -7% (long arms = longer pull path)";
          } else if (shortArms) {
            weight *= 1.05; // +5%
            adjustmentPercent = +5;
            adjustmentNote = "Adjusted +5% (short arms = shorter pull path)";
          }
        }

        if (lift === 'overhead') {
          if (longArms) {
            weight *= 0.92; // -8%
            adjustmentPercent = -8;
            adjustmentNote = "Adjusted -8% (long arms = longer ROM for lockout)";
          } else if (shortArms) {
            weight *= 1.10; // +10%
            adjustmentPercent = +10;
            adjustmentNote = "Adjusted +10% (short arms = shorter ROM, pressing advantage!)";
          }
        }

        // Round to nearest 5 lbs
        levels[level] = Math.round(weight / 5) * 5;
      });

      return { levels, adjustmentNote, adjustmentPercent };
    };

    return {
      squat: calculateLift('squat', standards.squat[sex]),
      deadlift: calculateLift('deadlift', standards.deadlift[sex]),
      clean: calculateLift('clean', standards.clean[sex]),
      snatch: calculateLift('snatch', standards.snatch[sex]),
      overhead: calculateLift('overhead', standards.overhead[sex])
    };
  };

  const generateSquatAdvice = (longLegs, shortLegs, longTorso, shortTorso, longFemur, shortFemur, longTibia, shortTibia, proportionData, buildTypes, shortArms, longArms) => {
    let advice = [];
    
    // COMPOUND BUILD TYPES - Check these FIRST before individual limb analysis
    const compoundBuildDetected = buildTypes.isPowerlifter || buildTypes.isDeadlifter || buildTypes.hasBackStrainRisk || buildTypes.isSquatGifted || buildTypes.isSquatChallenged;
    
    if (buildTypes.isPowerlifter) {
      // POWERLIFTER BUILD - Short arms + short femurs
      advice.push(`[BUILD] YOUR BUILD TYPE: Powerlifter Specialist (arms ${proportionData.armToHeight}%, femurs ${proportionData.femurToHeight}%)`);
      advice.push("");
      advice.push("[ADVANTAGE] ELITE SQUAT PROPORTIONS:");
      advice.push("- Short femurs = nearly vertical torso (15-30° lean)");
      advice.push("- Natural ATG depth capability");
      advice.push("- Lower center of mass = exceptional stability");
      advice.push("- This is YOUR money lift!");
      advice.push("");
      advice.push("[SETUP] YOUR OPTIMAL SETUP:");
      advice.push("- Standard shoulder-width stance (feels natural)");
      advice.push("- Toes 15-20° out");
      advice.push("- Bar stays over mid-foot effortlessly");
      advice.push("- Minimal forward lean needed");
      advice.push("");
      advice.push("[TIP] STRATEGY:");
      advice.push("- Prioritize squats in training");
      advice.push("- You have genetic advantage here");
      advice.push("- Focus on explosive power and bar speed");
      advice.push("- Pause squats and tempo work are excellent");
      advice.push("");
      advice.push("[INFO] SHORT ARMS NOTE:");
      advice.push("- Front rack (clean catch) needs extra mobility");
      advice.push("- Daily wrist/lat stretches recommended");
      advice.push("- But back squat = zero issues");
      advice.push("");
    } else if (buildTypes.isDeadlifter) {
      // DEADLIFTER BUILD - Long arms + long femurs
      advice.push(`[BUILD] YOUR BUILD TYPE: Deadlift Specialist (arms ${proportionData.armToHeight}%, femurs ${proportionData.femurToHeight}%)`);
      advice.push("");
      advice.push("[WARNING] SQUAT CHALLENGES:");
      advice.push("- Long femurs = significant forward lean (45-60°)");
      advice.push("- Requires strong upper back and core");
      advice.push("- Your limiting factor = torso strength");
      advice.push("- Squat won't be your best lift");
      advice.push("");
      advice.push("[SETUP] YOUR ADJUSTMENTS:");
      advice.push("- WIDE stance (10-20% more than shoulders)");
      advice.push("- Toes out 25-30° aggressively");
      advice.push("- Olympic shoes HIGHLY recommended (0.75-1\" heel)");
      advice.push("- Accept 45-60° forward lean as NORMAL");
      advice.push("");
      advice.push("[TIP] SMART STRATEGY:");
      advice.push("- Don't compare your squat to short-femured lifters");
      advice.push("- Your deadlift is your genetic gift");
      advice.push("- Focus on core/upper back strengthening");
      advice.push("- Film from side to verify bar path");
      advice.push("");
      advice.push("[ADVANTAGE] YOUR STRENGTH:");
      advice.push("- Long arms + long femurs = ELITE deadlift build");
      advice.push("- Focus on pulling movements");
      advice.push("");
    } else if (buildTypes.hasBackStrainRisk) {
      // BACK STRAIN RISK BUILD
      advice.push(`[BUILD] YOUR BUILD TYPE: Long Levers (femurs ${proportionData.femurToHeight}%, torso ${proportionData.sittingHeightRatio}%)`);
      advice.push("");
      advice.push("[WARNING] CRITICAL - INJURY PREVENTION:");
      advice.push("- Long femurs + long torso = LONGEST moment arm");
      advice.push("- Your lower back works MUCH harder than average");
      advice.push("- High injury risk if core isn't strong enough");
      advice.push("");
      advice.push("[SETUP] MANDATORY SAFETY:");
      advice.push("- Core/back strengthening is NON-NEGOTIABLE");
      advice.push("- Do planks, dead bugs, bird dogs daily");
      advice.push("- Belt use recommended for heavy sets");
      advice.push("- Progress weight slowly");
      advice.push("");
      advice.push("[SETUP] SQUAT ADJUSTMENTS:");
      advice.push("- Wider stance (10-20% more than shoulders)");
      advice.push("- Toes out 25-30°");
      advice.push("- Olympic shoes ESSENTIAL (0.75-1\" heel)");
      advice.push("- Accept 45-60° forward lean as normal");
      advice.push("");
      advice.push("[WARNING] DEADLIFT CAUTION:");
      advice.push("- Your deadlift has SAME back strain issue");
      advice.push("- Review deadlift section carefully");
      advice.push("- Never skip warm-ups");
      advice.push("");
    } else if (buildTypes.isSquatGifted) {
      // OLYMPIC SQUATTER BUILD
      const tibiaFemurDesc = proportionData.tibiaToFemur > 1.3 ? "long tibias" : "balanced to slightly long tibias";
      advice.push(`[BUILD] YOUR BUILD TYPE: Olympic Squatter (femurs ${proportionData.femurToHeight}%, tibia/femur ≈ ${proportionData.tibiaToFemur} – ${tibiaFemurDesc})`);
      advice.push("");
      advice.push("[ADVANTAGE] ELITE SQUAT GENETICS:");
      advice.push("- Best possible squat proportions!");
      advice.push("- Natural ATG (ass-to-grass) ability");
      advice.push("- Nearly vertical torso (15-25° lean)");
      advice.push("- Olympic lifting is your calling");
      advice.push("");
      advice.push("[SETUP] YOUR SETUP:");
      advice.push("- Standard shoulder-width stance");
      advice.push("- Can go narrower if you want");
      advice.push("- Toes 15-20° out");
      advice.push("- Knees track naturally - minimal cues needed");
      advice.push("");
      advice.push("[TIP] MAXIMIZE YOUR GIFT:");
      advice.push("- Focus on explosive power and speed");
      advice.push("- Practice pause squats at bottom");
      advice.push("- Your depth is your advantage - use it!");
      advice.push("- Consider Olympic weightlifting seriously");
      advice.push("");
    } else if (buildTypes.isSquatChallenged) {
      // SQUAT CHALLENGED BUILD (already detailed below, but add summary)
      advice.push(`[BUILD] YOUR BUILD TYPE: Squat Challenged (femurs ${proportionData.femurToHeight}%, tibia/femur ${proportionData.tibiaToFemur})`);
      advice.push("");
      advice.push("[WARNING] TOUGHEST SQUAT MECHANICS:");
      advice.push("- Long femurs combined with short tibias create the toughest squat mechanics");
      advice.push("- Requires a pronounced forward lean (45-60°)");
      advice.push("- Limits forward knee travel");
      advice.push("- This is purely leverage—not poor technique");
      advice.push("");
      advice.push("[SETUP] YOUR STRATEGY:");
      advice.push("- Olympic shoes MANDATORY (0.75-1\" heel)");
      advice.push("- Widest comfortable stance");
      advice.push("- Toes out 25-30°");
      advice.push("- Drive knees OUT aggressively");
      advice.push("- Accept forward lean as normal");
      advice.push("");
      advice.push("[MOBILITY] DAILY WORK:");
      advice.push("- Ankle dorsiflexion: Wall stretches (2x15 each side)");
      advice.push("- Hip flexors: Couch stretch, pigeon pose");
      advice.push("- Adductors: Wide stance stretches");
      advice.push("- Goblet squats: Practice depth daily");
      advice.push("");
      advice.push("[ADVANTAGE] SILVER LINING:");
      advice.push("- Long femurs = excellent for deadlifts");
      advice.push("- Focus on your stronger lifts");
      advice.push("");
    }
    
    // BUILD SUMMARY (individual limb analysis if no compound pattern detected)
    if (!compoundBuildDetected && longFemur) {
      advice.push(`[BUILD] YOUR BUILD: Long femurs (${proportionData.femurToHeight}% vs 25% avg, legs ${proportionData.legToHeight}%)`);
      advice.push("");
      advice.push("[WARNING] BIGGEST CHALLENGE:");
      advice.push("- Forward lean required (45-60° torso angle)");
      advice.push("- This IS NORMAL for your build");
      advice.push("- Upper back/core strength critical");
      advice.push("");
      advice.push("[SETUP] YOUR SETUP:");
      advice.push("- Stance: 10-20% wider than shoulders");
      advice.push("- Toes: Out 25-30°");
      advice.push("- Shoes: Olympic lifters (0.75-1\" heel) ESSENTIAL");
      advice.push("");
      advice.push("[MOBILITY] ESSENTIAL WORK:");
      advice.push("- Ankle mobility crucial (shoes help but not enough)");
      advice.push("- Hip flexor flexibility (allows depth)");
      advice.push("- Daily: Goblet squats to stretch");
      advice.push("");
      advice.push("[ADVANTAGE] SILVER LINING:");
      advice.push("- Long femurs = EXCELLENT for deadlifts");
    } else if (!compoundBuildDetected && shortFemur) {
      advice.push(`[BUILD] YOUR BUILD: Short femurs (${proportionData.femurToHeight}% vs 25% avg, legs ${proportionData.legToHeight}%)`);
      advice.push("");
      advice.push("[ADVANTAGE] YOUR ADVANTAGES:");
      advice.push("- IDEAL squat proportions");
      advice.push("- Nearly vertical torso (15-30° lean)");
      advice.push("- Can hit ATG depth easily");
      advice.push("");
      advice.push("[SETUP] YOUR SETUP:");
      advice.push("- Stance: Standard shoulder-width");
      advice.push("- Toes: 15-20° out");
      advice.push("");
      advice.push("[TIP] MAXIMIZE IT:");
      advice.push("- Focus on explosive power");
      advice.push("- Try pause squats and tempo work");
    } else if (buildTypes.isOlympicBuild && !buildTypes.isSquatGifted) {
      advice.push(`[BUILD] YOUR BUILD: Long-limbed Olympic build (arms ${proportionData.armToHeight}%, legs ${proportionData.legToHeight}%)`);
      advice.push("");
      advice.push("[WARNING] YOUR CHALLENGES:");
      advice.push("- Long legs = deep positions require mobility");
      advice.push("- Higher center of mass = less stable, requires more core control");
      advice.push("");
      advice.push("[SETUP] YOUR SOLUTIONS:");
      advice.push("- Olympic shoes with elevated heel ESSENTIAL");
      advice.push("- Wider stance (10-20% beyond shoulders)");
      advice.push("- Prioritize core and upper back strength");
      advice.push("- Accept more forward lean than shorter athletes");
      advice.push("- Long limbs make it harder to stay fully upright, so focus on balance and mid-foot control");
      advice.push("");
    } else if (!compoundBuildDetected && longArms) {
      advice.push(`[BUILD] YOUR BUILD: Balanced lower body, long-armed build (arms ${proportionData.armToHeight}%, legs ${proportionData.legToHeight}%)`);
      advice.push("");
      advice.push("[ADVANTAGE] SQUAT ADVANTAGE:");
      advice.push("- Balanced leg proportions = standard squat mechanics work");
      advice.push("- No extreme forward lean required");
      advice.push("");
      advice.push("[SETUP] YOUR SETUP:");
      advice.push("- Stance: Shoulder-width");
      advice.push("- Toes: 15-20° out");
      advice.push("");
      advice.push("[NOTE] ARM LENGTH IMPACT:");
      advice.push("- Long arms don't affect squat performance");
      advice.push("- Lower-body-dominant lifts aren't penalized by arm length");
    } else if (!compoundBuildDetected && shortArms) {
      advice.push(`[BUILD] YOUR BUILD: Short-armed, balanced-leg build (arms ${proportionData.armToHeight}%, legs ${proportionData.legToHeight}%)`);
      advice.push("");
      advice.push("[ADVANTAGE] SQUAT ADVANTAGE:");
      advice.push("- Balanced leg proportions = standard squat mechanics work");
      advice.push("- No extreme forward lean required");
      advice.push("");
      advice.push("[SETUP] YOUR SETUP:");
      advice.push("- Stance: Shoulder-width");
      advice.push("- Toes: 15-20° out");
      advice.push("");
      advice.push("[NOTE] ARM LENGTH IMPACT:");
      advice.push("- Short arms won't affect squat performance");
      advice.push("- See overhead press for your genetic advantage");
    } else if (!compoundBuildDetected) {
      advice.push(`[BUILD] YOUR BUILD: Well-proportioned (femurs ${proportionData.femurToHeight}% vs 25% avg, legs ${proportionData.legToHeight}%)`);
      advice.push("");
      advice.push("[ADVANTAGE] YOUR ADVANTAGES:");
      advice.push("- No extreme leverage challenges");
      advice.push("- Standard cues work well");
      advice.push("");
      advice.push("[SETUP] YOUR SETUP:");
      advice.push("- Stance: Shoulder-width");
      advice.push("- Toes: 15-20° out");
      advice.push("- Expect: 30-45° torso lean at depth");
    }
    
    // NEW: Knee position advice based on femur + tibia combo
    advice.push("");
    if (longFemur && shortTibia) {
      // WORST CASE - needs most aggressive knee-out
      advice.push(`[WARNING] KNEE POSITION CRITICAL: (tibia/femur ratio ${proportionData.tibiaToFemur})`);
      advice.push("- You have the HARDEST squat mechanics");
      advice.push("- Long femur + short shin = limited options");
      advice.push("- MUST drive knees OUT aggressively");
      advice.push("- Knees will want to cave - fight this!");
      advice.push("- Cue: 'Screw feet into ground'");
      advice.push("- Cue: 'Spread the floor apart'");
      advice.push("- Think: Knees OUT > Knees forward");
      advice.push("- Film from FRONT to check tracking");
    } else if (longFemur && !shortTibia) {
      // Long femur but normal/long tibia
      advice.push(`[SETUP] KNEE POSITION: (tibia/femur ratio ${proportionData.tibiaToFemur})`);
      advice.push("- Actively drive knees OUT throughout");
      advice.push("- Long femur = more valgus risk");
      advice.push("- Cue: 'Knees track over toes'");
      advice.push("- Engage glutes to prevent cave");
    } else if (shortFemur && longTibia) {
      // BEST CASE - minimal knee-out needed
      advice.push(`[ADVANTAGE] KNEE POSITION ADVANTAGE: (tibia/femur ratio ≈ ${proportionData.tibiaToFemur} – slightly long tibias, great knee tracking)`);
      advice.push("- Natural forward knee travel allowed");
      advice.push("- Elite mechanics for knee tracking");
      advice.push("- Cue: 'Knees follow toes naturally'");
      advice.push("- Very low valgus risk");
    } else if (shortFemur) {
      // Short femur with normal/short tibia
      advice.push(`[SETUP] KNEE POSITION: (tibia/femur ratio ${proportionData.tibiaToFemur})`);
      advice.push("- Less emphasis on 'knees out'");
      advice.push("- Cue: 'Keep knees tracking toes'");
      advice.push("- Natural alignment easier");
    } else if (longTibia) {
      // Average femur but long tibia
      advice.push(`[SETUP] KNEE POSITION: (tibia/femur ratio ${proportionData.tibiaToFemur})`);
      advice.push("- Good ankle mobility helps tracking");
      advice.push("- Moderate knee-out focus");
      advice.push("- Cue: 'Knees out and forward'");
    } else {
      // Everything average
      advice.push(`[SETUP] KNEE POSITION: (tibia/femur ratio ${proportionData.tibiaToFemur})`);
      advice.push("- Standard focus on knee tracking");
      advice.push("- Cue: 'Knees follow toes'");
      advice.push("- Watch for any inward collapse");
    }
    
    // Torso impact
    if (longTorso) {
      advice.push("");
      advice.push(`[INFO] LONG TORSO: (${proportionData.sittingHeightRatio}% vs 52% avg)`);
      advice.push("- ✅ Easier to stay upright in squat");
      advice.push("- ✅ Better leverage for squat depth");
      advice.push("- ✅ More stable base");
    } else if (shortTorso && !buildTypes.isOlympicBuild) {
      advice.push("");
      advice.push(`[INFO] TORSO IMPACT: (${proportionData.sittingHeightRatio}%)`);
      advice.push("- May need extra upper back work");
    }

    // Form checks
    advice.push("");
    advice.push("[FORM CHECK] FORM CHECKS:");
    advice.push("- Bar path stays vertical over mid-foot");
    advice.push("- Compare your angles to your build");
    advice.push("- Film from the side");
    
    // Front squat clarification
    advice.push("");
    advice.push("[INFO] FRONT SQUAT NOTE:");
    advice.push("- This advice is for BACK SQUAT (bar on upper back)");
    advice.push("- Front squat = different requirements");
    advice.push("- See Clean section for front rack mobility needs");
    
    if (shortArms) {
      advice.push("- Short arms = front squat challenge (extra wrist/lat work needed)");
    } else if (longArms) {
      advice.push("- Long arms = easier front rack but thoracic mobility key");
    } else {
      advice.push("- Average arms = neutral front rack mobility (standard stretches work)");
    }

    return advice;
  };

  const generateDeadliftAdvice = (longArms, shortArms, longLegs, longFemur, longTorso, shortFemur, proportionData, buildTypes) => {
    let advice = [];
    
    // COMPOUND BUILD TYPES - Check these FIRST
    const compoundBuildDetected = buildTypes.isDeadlifter || buildTypes.isPowerlifter || buildTypes.hasBackStrainRisk;
    
    if (buildTypes.isDeadlifter) {
      // ELITE DEADLIFT BUILD
      advice.push(`[BUILD] YOUR BUILD TYPE: Deadlift Specialist (arms ${proportionData.armToHeight}%, femurs ${proportionData.femurToHeight}%)`);
      advice.push("");
      advice.push("[ADVANTAGE] ELITE DEADLIFT GENETICS:");
      advice.push("- Long arms + long femurs = BUILT FOR DEADLIFTS");
      const romReduction = (Math.abs(47 - parseFloat(proportionData.armToHeight)) * 2).toFixed(0);
      if (romReduction < 5) {
        advice.push("- Slightly shorter ROM than average");
      } else {
        advice.push(`- ROM ~${romReduction}% shorter than average`);
      }
      advice.push("- Higher hip position natural");
      advice.push("- This is your BEST lift");
      advice.push("");
      advice.push("[SETUP] YOUR SETUP:");
      advice.push("- Conventional stance OPTIMAL");
      advice.push("- Hips start high (45-50° torso)");
      advice.push("- Back stays flatter naturally");
      advice.push("- Bar breaks floor easily");
      advice.push("");
      advice.push("[WARNING] WATCH OUT:");
      advice.push("- Grip endurance (long levers)");
      advice.push("- Train grip specifically");
      advice.push("- Consider straps for volume work");
      advice.push("");
      advice.push("[INFO] TRADEOFF:");
      advice.push("- Long femurs = squats will be harder");
      advice.push("- Accept deadlift as your dominant lift");
      advice.push("- Expect deadlift > squat (rare!)");
      advice.push("");
    } else if (buildTypes.isPowerlifter) {
      // POWERLIFTER BUILD (deadlift challenged)
      advice.push(`[BUILD] YOUR BUILD TYPE: Powerlifter Specialist (arms ${proportionData.armToHeight}%, femurs ${proportionData.femurToHeight}%)`);
      advice.push("");
      advice.push("[INFO] YOUR STRENGTHS:");
      advice.push("- Elite squat proportions (short femurs!)");
      advice.push("- Elite pressing proportions (short arms!)");
      advice.push("- Deadlift will be your weakest lift");
      advice.push("");
      advice.push("[WARNING] DEADLIFT CHALLENGE:");
      advice.push("- Short arms = can't reach bar easily");
      advice.push("- Short femurs would normally lower your hips, BUT...");
      advice.push("- Short arms counteract this—so your hips start moderately high");
      advice.push("- Net result: Conventional stance still very challenging");
      advice.push("");
      advice.push("[SETUP] SOLUTION - SUMO MANDATORY:");
      advice.push("- Conventional will force back rounding");
      advice.push("- Sumo reduces reach 20-30%");
      advice.push("- Feet: 1.5-2x shoulder width");
      advice.push("- Toes: 30-45° out");
      advice.push("- Knees pushed out (naturally required)");
      advice.push("");
      advice.push("[SETUP] IF CONVENTIONAL REQUIRED:");
      advice.push("- NARROW grip (hands just outside knees)");
      advice.push("- Push KNEES OUT aggressively");
      advice.push("- Keep shins more vertical");
      advice.push("- Saves ~2-3\" of reach");
      advice.push("- Start hips lower than feels natural");
      advice.push("");
      advice.push("[WARNING] REALITY CHECK:");
      advice.push("- Conventional always harder for you");
      advice.push("- Standards adjusted -12%");
      advice.push("- Not technique - it's pure leverage");
      advice.push("- Focus on squat/press dominance!");
      advice.push("");
    } else if (buildTypes.hasBackStrainRisk) {
      // BACK STRAIN RISK (mentioned in squat, reinforce here)
      advice.push(`[BUILD] YOUR BUILD TYPE: Long Levers (femurs ${proportionData.femurToHeight}%, torso ${proportionData.sittingHeightRatio}%)`);
      advice.push("");
      advice.push("[WARNING] CRITICAL - BACK INJURY RISK:");
      advice.push("- Long femurs + long torso = LONGEST moment arm");
      advice.push("- Your lower back under extreme stress");
      advice.push("- Same issue as in squats but MORE so in deadlifts");
      advice.push("- High injury risk without proper prep");
      advice.push("");
      advice.push("[SETUP] MANDATORY SAFETY:");
      advice.push("- Core work is NON-NEGOTIABLE");
      advice.push("- Belt strongly recommended");
      advice.push("- NEVER skip warm-up sets");
      advice.push("- Progress weight slowly (5-10 lbs max jumps)");
      advice.push("- Consider Romanian deadlifts instead");
      advice.push("");
      advice.push("[WARNING] FORM DISCIPLINE:");
      advice.push("- Keep back RIGID throughout");
      advice.push("- If form breaks, STOP immediately");
      advice.push("- Film every heavy set");
      advice.push("- Back pain = deload 20% and reset");
      advice.push("");
    }
    
    // Individual limb analysis (if no compound detected)
    if (!compoundBuildDetected && longArms) {
      advice.push(`[BUILD] YOUR BUILD: Long arms (${proportionData.armToHeight}% vs 45% avg)`);
      advice.push("");
      advice.push("[ADVANTAGE] YOUR ADVANTAGES:");
      advice.push("- Built for deadlifts!");
      const romReduction = (Math.abs(47 - parseFloat(proportionData.armToHeight)) * 2).toFixed(0);
      if (romReduction < 5) {
        advice.push("- Slightly shorter ROM than average");
      } else {
        advice.push(`- ROM ~${romReduction}% shorter than average`);
      }
      advice.push("- Conventional stance is OPTIMAL");
      if (longLegs) {
        advice.push("- Long arms + legs = ELITE DEADLIFT BUILD");
      }
      advice.push("");
      advice.push("[SETUP] YOUR SETUP:");
      advice.push("- Hips start higher");
      advice.push("- Back 30-40° from vertical");
      advice.push("");
      advice.push("[WARNING] WATCH OUT:");
      advice.push("- Grip endurance (longer levers)");
      advice.push("- Train grip strength");
    } else if (!compoundBuildDetected && shortArms) {
      advice.push(`[BUILD] YOUR BUILD: Short-armed lifter (${proportionData.armToHeight}% arm-to-height ratio)`);
      advice.push("");
      advice.push("[CHALLENGE] YOUR CHALLENGES:");
      advice.push("- Harder to reach bar → must drop hips lower");
      advice.push("- Higher back-rounding risk → engage lats early");
      advice.push("");
      advice.push("[SETUP] CONVENTIONAL SETUP:");
      advice.push("- Stance: Narrow (heels ≈6-8\")");
      advice.push("- Toes: 15-20° out");
      advice.push("- Grip: Just outside knees");
      advice.push("- Knee position: Push knees OUT for torso room");
      advice.push("- Hip height: Lower than feels natural (≈35-45° torso angle)");
      advice.push("- Lat engagement: Pull bar into shins before lift");
      advice.push("");
      advice.push("[MOBILITY & ASSISTANCE] DAILY WORK:");
      advice.push("- Hip-flexor and ankle mobility (daily)");
      advice.push("- Hamstring flexibility work");
      advice.push("- Practice: Paused deadlifts, front-loaded carries");
      advice.push("");
      advice.push("[ALTERNATIVES] OTHER OPTIONS:");
      advice.push("- Sumo → reduces reach 20-30%");
      advice.push("- Trap bar → easiest neutral setup");
      advice.push("- Block pulls → overload safely");
      advice.push("- Deficit pulls → skip (excessive ROM)");
    } else if (!compoundBuildDetected) {
      advice.push(`[BUILD] YOUR BUILD: Average arms (${proportionData.armToHeight}%)`);
      advice.push("");
      advice.push("[ADVANTAGE] YOUR ADVANTAGES:");
      advice.push("- Both stances viable");
      advice.push("- Try both to find preference");
      advice.push("");
      advice.push("[SETUP] CONVENTIONAL SETUP:");
      advice.push("- Shins about 1 inch from bar");
      advice.push("- Hips at natural height");
    }

    if (longLegs) {
      advice.push("");
      advice.push(`[INFO] LEG IMPACT: (${proportionData.legToHeight}% vs 48% avg)`);
      advice.push("- Higher starting hip position");
      advice.push("- Don't force hips too low");
    }
    
    if (longFemur && !buildTypes.isDeadlifter && !buildTypes.hasBackStrainRisk) {
      advice.push("");
      advice.push(`[INFO] LONG FEMUR NOTE: (${proportionData.femurToHeight}% vs 25% avg)`);
      advice.push("- Your hips will naturally start higher");
      advice.push("- Don't force them too low—let them find their natural position");
    }

    if (parseFloat(proportionData.sittingHeightRatio) > 54) {
      advice.push("");
      advice.push("[INFO] LONG TORSO WARNING:");
      advice.push(`- Long torso (${proportionData.sittingHeightRatio}%) = increased back strain`);
      advice.push("- Longer moment arm = more stress on lower back");
      advice.push("");
      advice.push("[SOLUTION] MANDATORY PREVENTION:");
      advice.push("- Belt use: HIGHLY recommended for heavy sets");
      advice.push("- Core work (daily): Planks, dead bugs, bird dogs");
      advice.push("- Back strengthening: Rows, reverse hypers");
      advice.push("- Good mornings: Build torso stability");
      advice.push("- Progress weight slowly (5-10 lbs jumps max)");
      advice.push("- Never skip warm-up sets");
    }

    return advice;
  };

  const generateCleanAdvice = (longArms, shortArms, longLegs, longTorso, shortFemur, longFemur, proportionData, buildTypes) => {
    let advice = [];
    
    advice.push("[BUILD] KEY CONCEPT:");
    advice.push("- First pull = deadlift mechanics");
    advice.push("- Catch = front squat mechanics");
    advice.push("");
    
    // COMPOUND BUILD TYPES
    if (buildTypes.isPowerlifter) {
      // POWERLIFTER BUILD (front rack challenge + great catch)
      advice.push(`[BUILD] YOUR BUILD: Powerlifter (arms ${proportionData.armToHeight}%, femurs ${proportionData.femurToHeight}%)`);
      advice.push("");
      advice.push("[WARNING] FRONT RACK CHALLENGE:");
      advice.push("- Short arms = harder to get elbows around");
      advice.push("- This is your most mobility-limited position");
      advice.push("- Front rack mobility is CRITICAL");
      advice.push("");
      advice.push("[ADVANTAGE] CATCH POSITION HELP:");
      advice.push("- Short femurs = GREAT for deep catch");
      advice.push("- Lower center of mass");
      advice.push("- Once you catch it, you'll stand it up");
      advice.push("");
      advice.push("[SETUP] YOUR PRIORITIES:");
      advice.push("1. Wrist flexibility daily (non-negotiable)");
      advice.push("2. Lat stretching");
      advice.push("3. Practice elbows-high position");
      advice.push("4. May need wider grip to get elbows around");
      advice.push("");
    } else if (buildTypes.isOlympicBuild) {
      // OLYMPIC BUILD (long pull + deep catch)
      advice.push(`[BUILD] YOUR BUILD: Olympic Lifter (long arms + long legs)`);
      advice.push("");
      advice.push("[WARNING] YOUR CHALLENGES:");
      advice.push("- Longer pull path (more distance to cover)");
      advice.push("- Deeper catch required (long legs)");
      advice.push("- Both phases require work");
      advice.push("");
      advice.push("[SETUP] YOUR SOLUTIONS:");
      advice.push("- Keep bar CLOSE during pull (critical!)");
      advice.push("- Olympic shoes ESSENTIAL for catch");
      advice.push("- Speed under bar is key");
      advice.push("- Keep chest high through the first pull to prevent hips shooting up early");
      advice.push("- Practice front squat depth separately");
      advice.push("");
      advice.push("[TIP] YOUR STRATEGY:");
      advice.push("- Compensate pull length with explosive speed");
      advice.push("- Your long arms help maintain bar speed and control");
      advice.push("- Focus on timing and positioning");
      advice.push("");
    } else if (longArms) {
      advice.push(`[WARNING] YOUR CHALLENGE: (arms ${proportionData.armToHeight}% vs 45%)`);
      advice.push("- Longer pull and more demanding turnover timing");
      advice.push("");
      advice.push("[SETUP] YOUR SOLUTION:");
      advice.push("- Keep bar close during pull");
      advice.push("- Your long arms help keep the bar close, but control speed so the turnover doesn't lag");
      advice.push("- Slightly wider grip for catch");
    } else if (longLegs) {
      advice.push(`[WARNING] YOUR CHALLENGE: (legs ${proportionData.legToHeight}% vs 48%)`);
      advice.push("- Deep catch position");
      advice.push("");
      advice.push("[SETUP] YOUR SOLUTION:");
      advice.push("- Olympic shoes ESSENTIAL");
      advice.push("- Practice front squat depth");
      advice.push("- Your challenge is the catch, not the pull");
    } else if (longFemur || buildTypes.isSquatChallenged) {
      advice.push(`[WARNING] YOUR CHALLENGE: (femurs ${proportionData.femurToHeight}% vs 25%)`);
      advice.push("- Long femurs make upright catch harder");
      advice.push("");
      advice.push("[SETUP] YOUR SOLUTION:");
      advice.push("- Wider stance in catch");
      advice.push("- Heeled shoes (0.75-1\" heel) ESSENTIAL");
      advice.push("- Practice front squat with wider stance");
    } else if (shortArms) {
      advice.push("[BUILD] YOUR BUILD: Short-armed build");
      advice.push("");
      advice.push("[WARNING] YOUR CHALLENGE:");
      advice.push("- Front-rack mobility is your limiter");
      advice.push("- Harder to get elbows around and up");
      advice.push("");
      advice.push("[SETUP] YOUR FOCUS:");
      advice.push("- Daily wrist and lat mobility work");
      advice.push("- May need slightly wider grip");
      advice.push("- Once you master the rack, catch is easier (balanced legs)");
    } else {
      advice.push("[ADVANTAGE] BALANCED BUILD:");
      advice.push("- Balanced leverages — standard technique works well");
      advice.push("- No special stance or grip adjustments required");
    }

    advice.push("");
    advice.push("[MOBILITY] FRONT RACK ESSENTIALS:");
    
    if (shortArms) {
      advice.push("- PRIORITY FOR SHORT ARMS: Wrist flexibility");
      advice.push("  • Wall wrist stretches: 3x30 seconds daily");
      advice.push("  • Barbell wrist rolls: 2x10 each direction");
      advice.push("  • High-elbow front rack holds: 3x20 seconds");
    } else if (longArms) {
      advice.push("- PRIORITY FOR LONG ARMS: Thoracic and lat flexibility");
      advice.push("  • Thoracic extensions over foam roller: 2 minutes");
      advice.push("  • Wall slides: 3x10 (emphasize overhead reach)");
      advice.push("  • Lat stretches against wall: 3x30 seconds");
      advice.push("  • Wrist mobility still useful but secondary");
    } else {
      advice.push("- Wrist flexibility: Important for everyone");
      advice.push("  • Wall wrist stretches: 3x30 seconds daily");
      advice.push("  • Barbell wrist rolls: 2x10 each direction");
    }
    
    if (!longArms) {
      // Only add this if not already covered in long arms section
      advice.push("- Lat flexibility: Allows elbows to rise (CRITICAL)");
      advice.push("  • Lat stretches against wall: 3x30 seconds");
      advice.push("  • Overhead reaches: Daily");
    }
    
    advice.push("- Shoulder mobility: Internal rotation needed");
    advice.push("  • Shoulder dislocations with band: 2x15");
    
    if (longLegs) {
      advice.push("- Ankle mobility: FOR YOU: deep catch required");
      advice.push("  • Ankle dorsiflexion drills: 2x15 each side");
    }
    
    advice.push("- Practice: Empty bar front squats daily");
    advice.push("");
    advice.push("🎯 CRITICAL FOR EVERYONE:");
    advice.push("- Elbows shoot through FAST and HIGH");
    advice.push("- Front rack position determines success");
    advice.push("- 5 minutes of mobility before every session");

    return advice;
  };

  const generateSnatchAdvice = (longArms, shortArms, longLegs, longFemur, shoulderWidth, height, proportionData, buildTypes) => {
    let advice = [];
    
    const shoulderToHeight = shoulderWidth / height;
    
    // COMPOUND BUILD TYPES
    if (buildTypes.isPowerlifter) {
      advice.push(`[BUILD] YOUR BUILD: Powerlifter (arms ${proportionData.armToHeight}%, femurs ${proportionData.femurToHeight}%)`);
      advice.push("");
      advice.push("[ADVANTAGE] YOUR ADVANTAGES:");
      advice.push("- Short arms = shorter pull path");
      advice.push("- Short femurs = easier receiving position");
      advice.push("- Both factors help!");
      advice.push("");
      advice.push("[SETUP] YOUR SETUP:");
      advice.push("- Don't need maximum grip width");
      advice.push("- Bar contacts upper thigh/hip comfortably");
      advice.push("- Natural depth in catch");
      advice.push("");
    } else if (buildTypes.isDeadlifter) {
      advice.push(`[BUILD] YOUR BUILD: Long-limbed deadlifter (arms ${proportionData.armToHeight}%, femurs ${proportionData.femurToHeight}%)`);
      advice.push("");
      advice.push("[CHALLENGES] YOUR CHALLENGES:");
      advice.push("- Longer pull path (+5-7%) demands higher bar speed");
      advice.push("- Long femurs make deep, upright catch positions harder");
      advice.push("- Both phases require extra work and timing");
      advice.push("");
      advice.push("[SOLUTIONS] YOUR SOLUTIONS:");
      advice.push("- WIDER snatch grip → shorter overhead distance");
      advice.push("- Olympic shoes ESSENTIAL → more knee travel, upright torso");
      advice.push("- Prioritize speed drills and timing work");
      advice.push("- Daily overhead squat practice (non-negotiable)");
      advice.push("");
      advice.push("[ADVANTAGE] YOUR STRENGTH:");
      advice.push("- Broad shoulders = superior overhead stability once locked out");
      advice.push("");
    } else if (longArms) {
      advice.push(`[BUILD] YOUR BUILD: Slightly long arms (${proportionData.armToHeight}% vs 45% avg)`);
      advice.push("");
      advice.push("[WARNING] YOUR CHALLENGE:");
      advice.push("- Longer pull path to overhead");
      advice.push("");
      advice.push("[SETUP] YOUR SOLUTION:");
      advice.push("- Use a wider snatch grip than average");
      advice.push("- Bar should contact hip crease when standing tall");
      advice.push("- Compensate with speed under bar");
      advice.push("");
      advice.push("[INFO] TRADE-OFF:");
      advice.push("- Wider grip increases wrist strain");
    } else if (shortArms) {
      advice.push(`[BUILD] YOUR BUILD: Short arms (${proportionData.armToHeight}% vs 45%)`);
      advice.push("");
      advice.push("[ADVANTAGE] YOUR ADVANTAGES:");
      advice.push("- Shorter pull path");
      advice.push("- Easier to generate speed");
      advice.push("");
      advice.push("[SETUP] YOUR SETUP:");
      advice.push("- Don't need max grip width");
      advice.push("- Bar should contact upper thigh or hip crease when standing tall");
    } else if (longFemur || buildTypes.isSquatChallenged) {
      advice.push(`[BUILD] YOUR BUILD: Long-femured lifter (femurs ${proportionData.femurToHeight}% vs 25%)`);
      advice.push("");
      advice.push("[WARNING] YOUR CHALLENGE:");
      advice.push("- Long femurs make deep, upright receiving position harder");
      advice.push("");
      advice.push("[SETUP] YOUR SOLUTION:");
      advice.push("- Wider stance in receiving position");
      advice.push("- Olympic shoes with elevated heel ESSENTIAL");
      advice.push("- Practice overhead squats with wider stance");
    } else {
      advice.push(`[BUILD] YOUR BUILD: Well-proportioned (arms ${proportionData.armToHeight}% = average)`);
      advice.push("");
      advice.push("[ADVANTAGE] YOUR ADVANTAGES:");
      advice.push("- Natural speed generation with standard technique");
      advice.push("");
      advice.push("[SETUP] YOUR SETUP:");
      advice.push("- Standard grip width — bar contacts upper-thigh/hip crease");
      advice.push("- Focus on bar staying close and smooth acceleration");
    }

    if (shoulderToHeight > 0.24) {
      advice.push("");
      advice.push("[ADVANTAGE] BONUS:");
      advice.push(`- Broad shoulders (${proportionData.shoulderToHeight}%)`);
      advice.push("- Great overhead stability");
    }

    if (longLegs) {
      advice.push("");
      advice.push("[WARNING] RECEIVING POSITION:");
      advice.push(`- Longer legs (${proportionData.legToHeight}%)`);
      advice.push("- Olympic shoes REQUIRED");
    }

    advice.push("");
    advice.push("[MOBILITY] CRITICAL AREAS:");
    advice.push("- Shoulder flexibility (overhead position)");
    advice.push("- Thoracic spine (allows bar behind head)");
    advice.push("- Wrist flexibility (wide-grip comfort)");
    advice.push("- Ankle mobility (deep receiving position)");
    advice.push("- Daily: Overhead squats 3×10, shoulder dislocations 3×15");
    advice.push("");
    advice.push("[LOCKOUT] PERFECT POSITION:");
    advice.push("- Bar slightly behind head");
    advice.push("- Shoulders active and elevated");
    advice.push("- Full extension at hips, knees, and ankles");

    return advice;
  };

  const generateOverheadAdvice = (longArms, shortArms, longTorso, proportionData, buildTypes) => {
    let advice = [];
    
    // COMPOUND BUILD (powerlifter = pressing dominance)
    if (buildTypes.isPowerlifter) {
      advice.push(`[BUILD] YOUR BUILD: Powerlifter (arms ${proportionData.armToHeight}%, femurs ${proportionData.femurToHeight}%)`);
      advice.push("");
      advice.push("[ADVANTAGE] ELITE PRESSING GENETICS:");
      advice.push("- Short arms = significantly less ROM");
      advice.push("- Lower center of mass (short femurs)");
      advice.push("- Ultra-stable base");
      advice.push("- This is one of your BEST lifts!");
      advice.push("");
      advice.push("[TIP] YOUR STRATEGY:");
      advice.push("- Prioritize pressing in training");
      advice.push("- Squat + Press = your genetic gifts");
      advice.push("- Volume work recovers faster (short ROM)");
      advice.push("- Natural pressing dominance");
      advice.push("");
    } else if (buildTypes.isDeadlifter) {
      const extraDistance = Math.abs(47 - parseFloat(proportionData.armToHeight)) * 5;
      advice.push(`[BUILD] YOUR BUILD: Long-armed deadlifter (arms ${proportionData.armToHeight}%, femurs ${proportionData.femurToHeight}%)`);
      advice.push("");
      advice.push("[WARNING] PRESSING CHALLENGE:");
      advice.push(`- Long arms = ≈${extraDistance.toFixed(0)}% more range of motion`);
      advice.push("- Lockout strength is the key limiter");
      advice.push("- Pressing won't match your pulling numbers");
      advice.push("");
      advice.push("[SETUP] YOUR FOCUS:");
      advice.push("- Prioritize triceps: Close-grip bench, dips, overhead extensions");
      advice.push("- Drive explosively from chest to forehead");
      advice.push("- Finish strong at lockout with overhead holds (3×20s)");
      advice.push("- Accept this isn't your genetic gift - deadlift is!");
      advice.push("");
    } else if (shortArms && longTorso) {
      advice.push(`[BUILD] YOUR BUILD: Elite Presser (arms ${proportionData.armToHeight}%, torso ${proportionData.sittingHeightRatio}%)`);
      advice.push("");
      advice.push("[ADVANTAGE] ELITE PRESSING GENETICS:");
      advice.push("- Short arms = significantly less ROM");
      advice.push("- Long torso = ultra-stable base");
      advice.push("- This is your BEST lift");
      advice.push("- Natural pressing dominance");
      advice.push("");
      advice.push("[TIP] MAXIMIZE YOUR GIFT:");
      advice.push("- Prioritize overhead press in training");
      advice.push("- Volume work recovers faster (short ROM)");
      advice.push("- Consider competing in strongman events");
      advice.push("- Your proportions are rare and valuable here");
      advice.push("");
    } else if (longArms) {
      advice.push(`[BUILD] YOUR BUILD: Long arms (${proportionData.armToHeight}% vs 45%)`);
      advice.push("");
      advice.push("[WARNING] YOUR CHALLENGE:");
      advice.push("- Noticeably longer range of motion than average");
      advice.push("- Lockout strength is the main limiter due to longer ROM");
      advice.push("");
      advice.push("[SETUP] YOUR SOLUTION:");
      advice.push("- Extra triceps work");
      advice.push("- Focus on finishing strong");
      advice.push("");
      advice.push("[ADVANTAGE] UPSIDE:");
      advice.push("- Easier clearance from shoulders");
    } else if (shortArms) {
      advice.push(`[BUILD] YOUR BUILD: Short arms (${proportionData.armToHeight}% vs 45%)`);
      advice.push("");
      advice.push("[ADVANTAGE] YOUR ADVANTAGES:");
      advice.push("- Natural pressing strength!");
      advice.push("- Press significantly less distance");
      advice.push("- This is your money lift");
      advice.push("");
      advice.push("[TIP] PRIORITIZE THIS:");
      advice.push("- Overhead work is your strength");
    } else {
      advice.push(`[BUILD] YOUR BUILD: Average arms (${proportionData.armToHeight}%)`);
      advice.push("");
      advice.push("[ADVANTAGE] YOUR ADVANTAGES:");
      advice.push("- Standard mechanics work well");
      advice.push("- Focus on technique");
    }

    if (longTorso) {
      advice.push("");
      advice.push("[INFO] TORSO BONUS:");
      advice.push(`- Long torso (${proportionData.sittingHeightRatio}%)`);
      advice.push("- Stable pressing base");
    }

    advice.push("");
    advice.push("[MOBILITY] SHOULDER HEALTH:");
    advice.push("- Shoulder mobility: CRITICAL for safe overhead position");
    advice.push("  • Shoulder dislocations with band: 2x15 daily");
    advice.push("  • Wall slides: 3x10");
    advice.push("- Thoracic extension: Allows vertical bar path");
    advice.push("  • Foam roll thoracic spine: 2 minutes");
    advice.push("  • Thoracic extensions over bench: 3x10");
    advice.push("- Lat flexibility: Prevents excessive arch");
    advice.push("  • Lat stretches against wall: 3x30 seconds");
    advice.push("  • Overhead reaches: Daily");
    advice.push("- Strengthening:");
    advice.push("  • Band pull-aparts: 3x20 (daily)");
    advice.push("  • Face pulls: 3x15");
    advice.push("  • Cuban rotations: 2x10");
    
    if (longArms) {
      advice.push("- Extra for long arms: Emphasis on lockout strength");
      advice.push("  • Overhead holds: 3x20 seconds");
    } else if (shortArms) {
      advice.push("- Extra for short arms: Maintain shoulder mobility");
      advice.push("  • Avoid over-arching to compensate for short ROM");
    }

    advice.push("");
    advice.push("[SETUP] PERFECT LOCKOUT:");
    advice.push("- Ears forward of biceps");
    advice.push("- Shoulders shrugged up");
    
    advice.push("");
    advice.push("⚠️ NOTE: Arm-length adjustments only apply where relevant;");
    advice.push("lower-body-dominant lifts (like the squat) aren't penalized for long arms.");

    return advice;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4 md:p-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Activity className="w-10 h-10 text-blue-400" />
            <h1 className="text-4xl font-bold text-white">Lifting Form Calculator</h1>
          </div>
          <p className="text-slate-300 text-lg">
            Get personalized lift recommendations based on your body proportions
          </p>
        </div>

        {!results ? (
          <>
            {/* Measurement Guide */}
            <div className="bg-slate-800 rounded-xl shadow-2xl p-6 md:p-8 mb-6 border border-slate-700">
              <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
                <Ruler className="w-6 h-6 text-blue-400" />
                How to Measure
              </h2>

              <div className="space-y-6">
                {/* Measurement Instructions */}
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Height */}
                  <div className="bg-slate-900 rounded-lg p-4 border border-slate-600">
                    <div className="flex items-start gap-3">

                      <div>
                        <h3 className="text-white font-bold mb-2">Total Height</h3>
                        <p className="text-slate-300 text-sm">
                          Stand straight against a wall. Measure from the floor to the top of your head.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Arms */}
                  <div className="bg-slate-900 rounded-lg p-4 border border-slate-600">
                    <div className="flex items-start gap-3">

                      <div>
                        <h3 className="text-white font-bold mb-2">Arm Length</h3>
                        <p className="text-slate-300 text-sm">
                          Let your arm hang naturally at your side. Measure from your shoulder joint (bony point) to your fingertip.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Sitting Height */}
                  <div className="bg-slate-900 rounded-lg p-4 border border-slate-600">
                    <div className="flex items-start gap-3">

                      <div>
                        <h3 className="text-white font-bold mb-2">Sitting Height</h3>
                        <p className="text-slate-300 text-sm">
                          Sit on the floor against a wall with legs straight out. Keep your back, shoulders, and head touching the wall. Measure from the floor to the top of your head.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Thigh */}
                  <div className="bg-slate-900 rounded-lg p-4 border border-slate-600">
                    <div className="flex items-start gap-3">

                      <div>
                        <h3 className="text-white font-bold mb-2">Thigh Length (Femur)</h3>
                        <p className="text-slate-300 text-sm">
                          Sit on a hard chair with feet flat on floor. Measure from where your hip crease bends (top of thigh) to the center of your kneecap.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Shoulders */}
                  <div className="bg-slate-900 rounded-lg p-4 border border-slate-600">
                    <div className="flex items-start gap-3">

                      <div>
                        <h3 className="text-white font-bold mb-2">Shoulder Width</h3>
                        <p className="text-slate-300 text-sm">
                          Measure across your back from one shoulder bone to the other (the bony points at the edge of each shoulder).
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-blue-900/30 border border-blue-700 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
                    <div className="text-blue-200 text-sm">
                      <strong>Pro Tip:</strong> Have someone help you measure for better accuracy. Use a cloth measuring tape and keep it flat against your body. You can use either inches or centimeters - just be consistent!
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Measurement Input Card */}
            <div className="bg-slate-800 rounded-xl shadow-2xl p-6 md:p-8 mb-6 border border-slate-700">
              <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
                <TrendingUp className="w-6 h-6 text-blue-400" />
                Enter Your Measurements
              </h2>

              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-slate-300 mb-2 font-medium">
                    Total Height
                  </label>
                  <input
                    type="number"
                    name="height"
                    value={measurements.height}
                    onChange={handleInputChange}
                    onWheel={handleNumberWheel}
                    className="w-full px-4 py-3 bg-slate-700 text-white rounded-lg border border-slate-600 focus:border-blue-400 focus:outline-none"
                    placeholder="e.g., 70"
                  />
                </div>

                <div>
                  <label className="block text-slate-300 mb-2 font-medium">
                    Arm Length (shoulder to fingertip)
                  </label>
                  <input
                    type="number"
                    name="armLength"
                    value={measurements.armLength}
                    onChange={handleInputChange}
                    onWheel={handleNumberWheel}
                    className="w-full px-4 py-3 bg-slate-700 text-white rounded-lg border border-slate-600 focus:border-blue-400 focus:outline-none"
                    placeholder="e.g., 30"
                  />
                </div>

                <div>
                  <label className="block text-slate-300 mb-2 font-medium">
                    Sitting Height (floor to top of head while seated)
                  </label>
                  <input
                    type="number"
                    name="sittingHeight"
                    value={measurements.sittingHeight}
                    onChange={handleInputChange}
                    onWheel={handleNumberWheel}
                    className="w-full px-4 py-3 bg-slate-700 text-white rounded-lg border border-slate-600 focus:border-blue-400 focus:outline-none"
                    placeholder="e.g., 34"
                  />
                </div>

                <div>
                  <label className="block text-slate-300 mb-2 font-medium">
                    Thigh Length (sitting hip to kneecap)
                  </label>
                  <input
                    type="number"
                    name="thighLength"
                    value={measurements.thighLength}
                    onChange={handleInputChange}
                    onWheel={handleNumberWheel}
                    className="w-full px-4 py-3 bg-slate-700 text-white rounded-lg border border-slate-600 focus:border-blue-400 focus:outline-none"
                    placeholder="e.g., 18"
                  />
                </div>

                <div>
                  <label className="block text-slate-300 mb-2 font-medium">
                    Shoulder Width
                  </label>
                  <input
                    type="number"
                    name="shoulderWidth"
                    value={measurements.shoulderWidth}
                    onChange={handleInputChange}
                    onWheel={handleNumberWheel}
                    className="w-full px-4 py-3 bg-slate-700 text-white rounded-lg border border-slate-600 focus:border-blue-400 focus:outline-none"
                    placeholder="e.g., 18"
                  />
                </div>

                <div>
                  <label className="block text-slate-300 mb-2 font-medium">
                    Bodyweight
                  </label>
                  <input
                    type="number"
                    name="bodyweight"
                    value={measurements.bodyweight}
                    onChange={handleInputChange}
                    onWheel={handleNumberWheel}
                    className="w-full px-4 py-3 bg-slate-700 text-white rounded-lg border border-slate-600 focus:border-blue-400 focus:outline-none"
                    placeholder="e.g., 180"
                  />
                </div>

                <div>
                  <label className="block text-slate-300 mb-2 font-medium">
                    Sex
                  </label>
                  <select
                    name="sex"
                    value={measurements.sex}
                    onChange={handleInputChange}
                    onWheel={handleNumberWheel}
                    className="w-full px-4 py-3 bg-slate-700 text-white rounded-lg border border-slate-600 focus:border-blue-400 focus:outline-none"
                  >
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                  </select>
                </div>

                <div>
                  <label className="block text-slate-300 mb-2 font-medium">
                    Unit of Measurement
                  </label>
                  <select
                    name="unit"
                    value={measurements.unit}
                    onChange={handleInputChange}
                    onWheel={handleNumberWheel}
                    className="w-full px-4 py-3 bg-slate-700 text-white rounded-lg border border-slate-600 focus:border-blue-400 focus:outline-none"
                  >
                    <option value="inches">Inches</option>
                    <option value="cm">Centimeters</option>
                  </select>
                </div>
              </div>

              <button
                onClick={analyzeMeasurements}
                className="w-full bg-blue-500 hover:bg-blue-600 text-white font-bold py-4 px-6 rounded-lg transition-colors duration-200 shadow-lg hover:shadow-xl"
              >
                Analyze My Form
              </button>
            </div>
          </>
        ) : (
          <div className="space-y-6">
            {/* Back Button */}
            <button
              onClick={() => setResults(null)}
              className="text-blue-400 hover:text-blue-300 flex items-center gap-2 mb-4"
            >
              ← Back to Measurements
            </button>

            {/* Body Type Summary */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-800 rounded-xl shadow-2xl p-6 border border-blue-500">
              <h3 className="text-2xl font-bold text-white mb-4">Your Body Type Analysis</h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 text-white">
                <div className="bg-white/10 rounded-lg p-4">
                  <div className="text-sm opacity-90">Arm-to-Height Ratio</div>
                  <div className="text-2xl font-bold">{results.proportions.armToHeight}%</div>
                  <div className="text-xs opacity-75 mt-1">
                    {parseFloat(results.proportions.armToHeight) > 47 ? "Long arms" : 
                     parseFloat(results.proportions.armToHeight) < 43 ? "Short arms" : "Average"}
                    {" • Avg: 45%"}
                  </div>
                </div>
                <div className="bg-white/10 rounded-lg p-4">
                  <div className="text-sm opacity-90">Leg-to-Height Ratio</div>
                  <div className="text-2xl font-bold">{results.proportions.legToHeight}%</div>
                  <div className="text-xs opacity-75 mt-1">
                    {parseFloat(results.proportions.legToHeight) > 50 ? "Long legs" : 
                     parseFloat(results.proportions.legToHeight) < 46 ? "Short legs" : "Average"}
                    {" • Avg: 48%"}
                  </div>
                </div>
                <div className="bg-white/10 rounded-lg p-4">
                  <div className="text-sm opacity-90">Sitting Height Ratio</div>
                  <div className="text-2xl font-bold">{results.proportions.sittingHeightRatio}%</div>
                  <div className="text-xs opacity-75 mt-1">
                    {parseFloat(results.proportions.sittingHeightRatio) > 54 ? "Long torso" : 
                     parseFloat(results.proportions.sittingHeightRatio) < 50 ? "Short torso" : "Average"}
                    {" • Avg: 52%"}
                  </div>
                </div>
                <div className="bg-white/10 rounded-lg p-4">
                  <div className="text-sm opacity-90">Femur-to-Height Ratio</div>
                  <div className="text-2xl font-bold">{results.proportions.femurToHeight}%</div>
                  <div className="text-xs opacity-75 mt-1">
                    {parseFloat(results.proportions.femurToHeight) > 27 ? "Long femurs" : 
                     parseFloat(results.proportions.femurToHeight) < 23 ? "Short femurs" : "Balanced"}
                    {" • Avg: 25%"}
                  </div>
                </div>
                <div className="bg-white/10 rounded-lg p-4">
                  <div className="text-sm opacity-90">Tibia-to-Femur Ratio</div>
                  <div className="text-2xl font-bold">{results.proportions.tibiaToFemur}</div>
                  <div className="text-xs opacity-75 mt-1">
                    {parseFloat(results.proportions.tibiaToFemur) > 1.30 ? "Long tibias (Good for squats!)" : 
                     parseFloat(results.proportions.tibiaToFemur) < 1.10 ? "Short tibias (Squat challenge)" : "Balanced"}
                    {" • Avg: 1.15-1.25"}
                  </div>
                </div>
                <div className="bg-white/10 rounded-lg p-4">
                  <div className="text-sm opacity-90">Shoulder-to-Height Ratio</div>
                  <div className="text-2xl font-bold">{results.proportions.shoulderToHeight}%</div>
                  <div className="text-xs opacity-75 mt-1">
                    {parseFloat(results.proportions.shoulderToHeight) > 24 ? "Broad shoulders" : 
                     parseFloat(results.proportions.shoulderToHeight) < 20 ? "Narrow shoulders" : "Average"}
                    {" • Avg: 22%"}
                  </div>
                </div>
              </div>
              <div className="mt-4 text-white text-lg font-medium">
                Classification: {results.proportions.type}
              </div>
            </div>

            {/* Squat Recommendations */}
            <div className="bg-slate-800 rounded-xl shadow-2xl border border-slate-700 overflow-hidden">
              <button
                onClick={() => toggleSection('squat')}
                className="w-full p-6 text-left hover:bg-slate-700/50 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-white mb-2 flex items-center gap-2">
                      {expandedSections.squat ? '▼' : '▶'} BACK SQUAT
                    </h3>
                    {!expandedSections.squat && (
                      <div className="text-sm text-slate-400">
                        {getLiftSummary(results.squat, results.strengthStandards.squat).buildText} • Standards: {getLiftSummary(results.squat, results.strengthStandards.squat).standardsRange} (1RM)
                      </div>
                    )}
                  </div>
                </div>
              </button>
              
              {expandedSections.squat && (
                <div className="px-6 pb-6">
                  <div className="space-y-2">
                    {results.squat.map((tip, index) => {
                      // Style based on tag type
                      let className = "text-slate-300 leading-relaxed ";
                      if (tip.startsWith('[BUILD]')) className += "text-blue-400 font-bold text-base mb-2";
                      else if (tip.startsWith('[WARNING]')) className += "text-yellow-400 font-semibold";
                      else if (tip.startsWith('[ADVANTAGE]')) className += "text-green-400 font-semibold";
                      else if (tip.startsWith('[SETUP]')) className += "text-cyan-400 font-semibold";
                      else if (tip.startsWith('[INFO]')) className += "text-purple-400 font-semibold";
                      else if (tip.startsWith('[FORM CHECK]')) className += "text-orange-400 font-semibold";
                      else if (tip.startsWith('[TIP]')) className += "text-pink-400 font-semibold";
                      else if (tip.startsWith('-')) className += "text-slate-300 pl-4";
                      else if (tip === '') return <div key={index} className="h-2" />;
                      
                      return (
                        <div key={index} className={className}>
                          {tip}
                        </div>
                      );
                    })}
                  </div>

                  {/* Strength Standards for Squat */}
                  <div className="mt-6 pt-6 border-t border-slate-700">
                    <h4 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
                      💪 Strength Standards (1RM)
                      <span className="text-sm font-normal text-slate-400">({results.bodyweight} lbs)</span>
                    </h4>
                    <div className="overflow-x-auto">
                      <table className="w-full text-left">
                        <thead>
                          <tr className="border-b border-slate-600">
                            <th className="pb-2 text-slate-300 font-semibold">Level</th>
                            <th className="pb-2 text-slate-300 font-semibold text-right">Weight</th>
                          </tr>
                        </thead>
                        <tbody className="text-slate-300">
                          <tr className="border-b border-slate-700">
                            <td className="py-2">Novice</td>
                            <td className="py-2 text-right font-mono">{results.strengthStandards.squat.levels.novice} lbs</td>
                          </tr>
                          <tr className="border-b border-slate-700">
                            <td className="py-2">Beginner</td>
                            <td className="py-2 text-right font-mono">{results.strengthStandards.squat.levels.beginner} lbs</td>
                          </tr>
                          <tr className="border-b border-slate-700">
                            <td className="py-2">Intermediate</td>
                            <td className="py-2 text-right font-mono">{results.strengthStandards.squat.levels.intermediate} lbs</td>
                          </tr>
                          <tr className="border-b border-slate-700">
                            <td className="py-2">Advanced</td>
                            <td className="py-2 text-right font-mono">{results.strengthStandards.squat.levels.advanced} lbs</td>
                          </tr>
                          <tr>
                            <td className="py-2">Elite</td>
                            <td className="py-2 text-right font-mono">{results.strengthStandards.squat.levels.elite} lbs</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    {results.strengthStandards.squat.adjustmentNote && (
                      <div className="mt-3 text-sm text-cyan-400 bg-cyan-900/20 rounded p-2">
                        {results.strengthStandards.squat.adjustmentPercent > 0 ? '✓' : '⚠'} {results.strengthStandards.squat.adjustmentNote}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Deadlift Recommendations */}
            <div className="bg-slate-800 rounded-xl shadow-2xl border border-slate-700 overflow-hidden">
              <button
                onClick={() => toggleSection('deadlift')}
                className="w-full p-6 text-left hover:bg-slate-700/50 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-white mb-2 flex items-center gap-2">
                      {expandedSections.deadlift ? '▼' : '▶'} DEADLIFT
                    </h3>
                    {!expandedSections.deadlift && (
                      <div className="text-sm text-slate-400">
                        {getLiftSummary(results.deadlift, results.strengthStandards.deadlift).buildText} • Standards: {getLiftSummary(results.deadlift, results.strengthStandards.deadlift).standardsRange} (1RM)
                      </div>
                    )}
                  </div>
                </div>
              </button>
              
              {expandedSections.deadlift && (
                <div className="px-6 pb-6">
                  <div className="space-y-2">
                    {results.deadlift.map((tip, index) => {
                      // Style based on tag type
                      let className = "text-slate-300 leading-relaxed ";
                      if (tip.startsWith('[BUILD]')) className += "text-blue-400 font-bold text-base mb-2";
                      else if (tip.startsWith('[WARNING]')) className += "text-yellow-400 font-semibold";
                      else if (tip.startsWith('[ADVANTAGE]')) className += "text-green-400 font-semibold";
                      else if (tip.startsWith('[SETUP]')) className += "text-cyan-400 font-semibold";
                      else if (tip.startsWith('[INFO]')) className += "text-purple-400 font-semibold";
                      else if (tip.startsWith('[FORM CHECK]')) className += "text-orange-400 font-semibold";
                      else if (tip.startsWith('[TIP]')) className += "text-pink-400 font-semibold";
                      else if (tip.startsWith('-')) className += "text-slate-300 pl-4";
                      else if (tip === '') return <div key={index} className="h-2" />;
                      
                      return (
                        <div key={index} className={className}>
                          {tip}
                        </div>
                      );
                    })}
                  </div>

                  {/* Strength Standards for Deadlift */}
                  <div className="mt-6 pt-6 border-t border-slate-700">
                    <h4 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
                      💪 Strength Standards (1RM)
                      <span className="text-sm font-normal text-slate-400">({results.bodyweight} lbs)</span>
                    </h4>
                    <div className="overflow-x-auto">
                      <table className="w-full text-left">
                        <thead>
                          <tr className="border-b border-slate-600">
                            <th className="pb-2 text-slate-300 font-semibold">Level</th>
                            <th className="pb-2 text-slate-300 font-semibold text-right">Weight</th>
                          </tr>
                        </thead>
                        <tbody className="text-slate-300">
                          <tr className="border-b border-slate-700">
                            <td className="py-2">Novice</td>
                            <td className="py-2 text-right font-mono">{results.strengthStandards.deadlift.levels.novice} lbs</td>
                          </tr>
                          <tr className="border-b border-slate-700">
                            <td className="py-2">Beginner</td>
                            <td className="py-2 text-right font-mono">{results.strengthStandards.deadlift.levels.beginner} lbs</td>
                          </tr>
                          <tr className="border-b border-slate-700">
                            <td className="py-2">Intermediate</td>
                            <td className="py-2 text-right font-mono">{results.strengthStandards.deadlift.levels.intermediate} lbs</td>
                          </tr>
                          <tr className="border-b border-slate-700">
                            <td className="py-2">Advanced</td>
                            <td className="py-2 text-right font-mono">{results.strengthStandards.deadlift.levels.advanced} lbs</td>
                          </tr>
                          <tr>
                            <td className="py-2">Elite</td>
                            <td className="py-2 text-right font-mono">{results.strengthStandards.deadlift.levels.elite} lbs</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    {results.strengthStandards.deadlift.adjustmentNote && (
                      <div className="mt-3 text-sm text-cyan-400 bg-cyan-900/20 rounded p-2">
                        {results.strengthStandards.deadlift.adjustmentPercent > 0 ? '✓' : '⚠'} {results.strengthStandards.deadlift.adjustmentNote}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Clean Recommendations */}
            <div className="bg-slate-800 rounded-xl shadow-2xl border border-slate-700 overflow-hidden">
              <button
                onClick={() => toggleSection('clean')}
                className="w-full p-6 text-left hover:bg-slate-700/50 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-white mb-2 flex items-center gap-2">
                      {expandedSections.clean ? '▼' : '▶'} CLEAN
                    </h3>
                    {!expandedSections.clean && (
                      <div className="text-sm text-slate-400">
                        {getLiftSummary(results.clean, results.strengthStandards.clean).buildText} • Standards: {getLiftSummary(results.clean, results.strengthStandards.clean).standardsRange} (1RM)
                      </div>
                    )}
                  </div>
                </div>
              </button>
              
              {expandedSections.clean && (
                <div className="px-6 pb-6">
                  <div className="space-y-2">
                    {results.clean.map((tip, index) => {
                      // Style based on tag type
                      let className = "text-slate-300 leading-relaxed ";
                      if (tip.startsWith('[BUILD]')) className += "text-blue-400 font-bold text-base mb-2";
                      else if (tip.startsWith('[WARNING]')) className += "text-yellow-400 font-semibold";
                      else if (tip.startsWith('[ADVANTAGE]')) className += "text-green-400 font-semibold";
                      else if (tip.startsWith('[SETUP]')) className += "text-cyan-400 font-semibold";
                      else if (tip.startsWith('[INFO]')) className += "text-purple-400 font-semibold";
                      else if (tip.startsWith('[FORM CHECK]')) className += "text-orange-400 font-semibold";
                      else if (tip.startsWith('[TIP]')) className += "text-pink-400 font-semibold";
                      else if (tip.startsWith('-')) className += "text-slate-300 pl-4";
                      else if (tip === '') return <div key={index} className="h-2" />;
                      
                      return (
                        <div key={index} className={className}>
                          {tip}
                        </div>
                      );
                    })}
                  </div>

                  {/* Strength Standards for Clean */}
                  <div className="mt-6 pt-6 border-t border-slate-700">
                    <h4 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
                      💪 Strength Standards (1RM)
                      <span className="text-sm font-normal text-slate-400">({results.bodyweight} lbs)</span>
                    </h4>
                    <div className="overflow-x-auto">
                      <table className="w-full text-left">
                        <thead>
                          <tr className="border-b border-slate-600">
                            <th className="pb-2 text-slate-300 font-semibold">Level</th>
                            <th className="pb-2 text-slate-300 font-semibold text-right">Weight</th>
                          </tr>
                        </thead>
                        <tbody className="text-slate-300">
                          <tr className="border-b border-slate-700">
                            <td className="py-2">Novice</td>
                            <td className="py-2 text-right font-mono">{results.strengthStandards.clean.levels.novice} lbs</td>
                          </tr>
                          <tr className="border-b border-slate-700">
                            <td className="py-2">Beginner</td>
                            <td className="py-2 text-right font-mono">{results.strengthStandards.clean.levels.beginner} lbs</td>
                          </tr>
                          <tr className="border-b border-slate-700">
                            <td className="py-2">Intermediate</td>
                            <td className="py-2 text-right font-mono">{results.strengthStandards.clean.levels.intermediate} lbs</td>
                          </tr>
                          <tr className="border-b border-slate-700">
                            <td className="py-2">Advanced</td>
                            <td className="py-2 text-right font-mono">{results.strengthStandards.clean.levels.advanced} lbs</td>
                          </tr>
                          <tr>
                            <td className="py-2">Elite</td>
                            <td className="py-2 text-right font-mono">{results.strengthStandards.clean.levels.elite} lbs</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    {results.strengthStandards.clean.adjustmentNote && (
                      <div className="mt-3 text-sm text-cyan-400 bg-cyan-900/20 rounded p-2">
                        {results.strengthStandards.clean.adjustmentPercent > 0 ? '✓' : '⚠'} {results.strengthStandards.clean.adjustmentNote}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Snatch Recommendations */}
            <div className="bg-slate-800 rounded-xl shadow-2xl border border-slate-700 overflow-hidden">
              <button
                onClick={() => toggleSection('snatch')}
                className="w-full p-6 text-left hover:bg-slate-700/50 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-white mb-2 flex items-center gap-2">
                      {expandedSections.snatch ? '▼' : '▶'} SNATCH
                    </h3>
                    {!expandedSections.snatch && (
                      <div className="text-sm text-slate-400">
                        {getLiftSummary(results.snatch, results.strengthStandards.snatch).buildText} • Standards: {getLiftSummary(results.snatch, results.strengthStandards.snatch).standardsRange} (1RM)
                      </div>
                    )}
                  </div>
                </div>
              </button>
              
              {expandedSections.snatch && (
                <div className="px-6 pb-6">
                  <div className="space-y-2">
                    {results.snatch.map((tip, index) => {
                      // Style based on tag type
                      let className = "text-slate-300 leading-relaxed ";
                      if (tip.startsWith('[BUILD]')) className += "text-blue-400 font-bold text-base mb-2";
                      else if (tip.startsWith('[WARNING]')) className += "text-yellow-400 font-semibold";
                      else if (tip.startsWith('[ADVANTAGE]')) className += "text-green-400 font-semibold";
                      else if (tip.startsWith('[SETUP]')) className += "text-cyan-400 font-semibold";
                      else if (tip.startsWith('[INFO]')) className += "text-purple-400 font-semibold";
                      else if (tip.startsWith('[FORM CHECK]')) className += "text-orange-400 font-semibold";
                      else if (tip.startsWith('[TIP]')) className += "text-pink-400 font-semibold";
                      else if (tip.startsWith('-')) className += "text-slate-300 pl-4";
                      else if (tip === '') return <div key={index} className="h-2" />;
                      
                      return (
                        <div key={index} className={className}>
                          {tip}
                        </div>
                      );
                    })}
                  </div>

                  {/* Strength Standards for Snatch */}
                  <div className="mt-6 pt-6 border-t border-slate-700">
                    <h4 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
                      💪 Strength Standards (1RM)
                      <span className="text-sm font-normal text-slate-400">({results.bodyweight} lbs)</span>
                    </h4>
                    <div className="overflow-x-auto">
                      <table className="w-full text-left">
                        <thead>
                          <tr className="border-b border-slate-600">
                            <th className="pb-2 text-slate-300 font-semibold">Level</th>
                            <th className="pb-2 text-slate-300 font-semibold text-right">Weight</th>
                          </tr>
                        </thead>
                        <tbody className="text-slate-300">
                          <tr className="border-b border-slate-700">
                            <td className="py-2">Novice</td>
                            <td className="py-2 text-right font-mono">{results.strengthStandards.snatch.levels.novice} lbs</td>
                          </tr>
                          <tr className="border-b border-slate-700">
                            <td className="py-2">Beginner</td>
                            <td className="py-2 text-right font-mono">{results.strengthStandards.snatch.levels.beginner} lbs</td>
                          </tr>
                          <tr className="border-b border-slate-700">
                            <td className="py-2">Intermediate</td>
                            <td className="py-2 text-right font-mono">{results.strengthStandards.snatch.levels.intermediate} lbs</td>
                          </tr>
                          <tr className="border-b border-slate-700">
                            <td className="py-2">Advanced</td>
                            <td className="py-2 text-right font-mono">{results.strengthStandards.snatch.levels.advanced} lbs</td>
                          </tr>
                          <tr>
                            <td className="py-2">Elite</td>
                            <td className="py-2 text-right font-mono">{results.strengthStandards.snatch.levels.elite} lbs</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    {results.strengthStandards.snatch.adjustmentNote && (
                      <div className="mt-3 text-sm text-cyan-400 bg-cyan-900/20 rounded p-2">
                        {results.strengthStandards.snatch.adjustmentPercent > 0 ? '✓' : '⚠'} {results.strengthStandards.snatch.adjustmentNote}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Overhead Press Recommendations */}
            <div className="bg-slate-800 rounded-xl shadow-2xl border border-slate-700 overflow-hidden">
              <button
                onClick={() => toggleSection('overhead')}
                className="w-full p-6 text-left hover:bg-slate-700/50 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-white mb-2 flex items-center gap-2">
                      {expandedSections.overhead ? '▼' : '▶'} OVERHEAD PRESS
                    </h3>
                    {!expandedSections.overhead && (
                      <div className="text-sm text-slate-400">
                        {getLiftSummary(results.overhead, results.strengthStandards.overhead).buildText} • Standards: {getLiftSummary(results.overhead, results.strengthStandards.overhead).standardsRange} (1RM)
                      </div>
                    )}
                  </div>
                </div>
              </button>
              
              {expandedSections.overhead && (
                <div className="px-6 pb-6">
                  <div className="space-y-2">
                    {results.overhead.map((tip, index) => {
                      // Style based on tag type
                      let className = "text-slate-300 leading-relaxed ";
                      if (tip.startsWith('[BUILD]')) className += "text-blue-400 font-bold text-base mb-2";
                      else if (tip.startsWith('[WARNING]')) className += "text-yellow-400 font-semibold";
                      else if (tip.startsWith('[ADVANTAGE]')) className += "text-green-400 font-semibold";
                      else if (tip.startsWith('[SETUP]')) className += "text-cyan-400 font-semibold";
                      else if (tip.startsWith('[INFO]')) className += "text-purple-400 font-semibold";
                      else if (tip.startsWith('[FORM CHECK]')) className += "text-orange-400 font-semibold";
                      else if (tip.startsWith('[TIP]')) className += "text-pink-400 font-semibold";
                      else if (tip.startsWith('-')) className += "text-slate-300 pl-4";
                      else if (tip === '') return <div key={index} className="h-2" />;
                      
                      return (
                        <div key={index} className={className}>
                          {tip}
                        </div>
                      );
                    })}
                  </div>

                  {/* Strength Standards for Overhead Press */}
                  <div className="mt-6 pt-6 border-t border-slate-700">
                    <h4 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
                      💪 Strength Standards (1RM)
                      <span className="text-sm font-normal text-slate-400">({results.bodyweight} lbs)</span>
                    </h4>
                    <div className="overflow-x-auto">
                      <table className="w-full text-left">
                        <thead>
                          <tr className="border-b border-slate-600">
                            <th className="pb-2 text-slate-300 font-semibold">Level</th>
                            <th className="pb-2 text-slate-300 font-semibold text-right">Weight</th>
                          </tr>
                        </thead>
                        <tbody className="text-slate-300">
                          <tr className="border-b border-slate-700">
                            <td className="py-2">Novice</td>
                            <td className="py-2 text-right font-mono">{results.strengthStandards.overhead.levels.novice} lbs</td>
                          </tr>
                          <tr className="border-b border-slate-700">
                            <td className="py-2">Beginner</td>
                            <td className="py-2 text-right font-mono">{results.strengthStandards.overhead.levels.beginner} lbs</td>
                          </tr>
                          <tr className="border-b border-slate-700">
                            <td className="py-2">Intermediate</td>
                            <td className="py-2 text-right font-mono">{results.strengthStandards.overhead.levels.intermediate} lbs</td>
                          </tr>
                          <tr className="border-b border-slate-700">
                            <td className="py-2">Advanced</td>
                            <td className="py-2 text-right font-mono">{results.strengthStandards.overhead.levels.advanced} lbs</td>
                          </tr>
                          <tr>
                            <td className="py-2">Elite</td>
                            <td className="py-2 text-right font-mono">{results.strengthStandards.overhead.levels.elite} lbs</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    {results.strengthStandards.overhead.adjustmentNote && (
                      <div className="mt-3 text-sm text-cyan-400 bg-cyan-900/20 rounded p-2">
                        {results.strengthStandards.overhead.adjustmentPercent > 0 ? '✓' : '⚠'} {results.strengthStandards.overhead.adjustmentNote}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Disclaimer */}
            <div className="bg-yellow-900/30 border border-yellow-700 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-yellow-400 mt-0.5 flex-shrink-0" />
                <p className="text-yellow-200 text-sm">
                  <strong>Important:</strong> These recommendations are general guidelines based on common biomechanical principles. 
                  Always work with a qualified strength coach to refine your technique and ensure proper form. Individual factors like mobility, 
                  strength imbalances, and injury history also play crucial roles in optimal lifting form.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
